import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { Modal } from './Modal.js';
import { Button } from './Button.js';
import { marketplaceApi } from '../../services/api';

type EntityType = 'workflow' | 'agent_template' | 'tool';

interface PublishToMarketplaceModalProps {
  entityType: EntityType;
  entityId: number | string;
  entityName: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ENTITY_LABELS: Record<EntityType, string> = {
  workflow: 'Workflow',
  agent_template: 'Agent Template',
  tool: 'Tool Library',
};

const PRICING_MODELS = [
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
] as const;

export const PublishToMarketplaceModal: React.FC<PublishToMarketplaceModalProps> = ({
  entityType,
  entityId,
  entityName,
  open,
  onClose,
  onSuccess,
}) => {
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [pricingModel, setPricingModel] = useState<string>('free');
  const [priceUsd, setPriceUsd] = useState('');
  const [contentAccess, setContentAccess] = useState<'public' | 'private'>('private');
  const [successMessage, setSuccessMessage] = useState('');

  const publishMutation = useMutation({
    mutationFn: () =>
      // entityType is derived server-side from the entity itself — the listing only names the entity.
      marketplaceApi.publish({
        entityId: Number(entityId),
        title: entityName,
        description: description || undefined,
        category: category || undefined,
        tags: tags
          ? tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
        pricingModel: pricingModel === 'paid' ? 'one_time' : 'free',
        priceUsd: pricingModel === 'paid' && priceUsd ? priceUsd : undefined,
        contentAccess,
      }),
    onSuccess: () => {
      setSuccessMessage(`"${entityName}" published to marketplace!`);
      setTimeout(() => {
        handleClose();
        onSuccess?.();
      }, 1500);
    },
  });

  const handleClose = () => {
    setDescription('');
    setCategory('');
    setTags('');
    setPricingModel('free');
    setPriceUsd('');
    setSuccessMessage('');
    publishMutation.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Publish to Marketplace"
      titleIcon={<Upload size={16} className="text-accent-text" />}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => publishMutation.mutate()}
            loading={publishMutation.isPending}
            disabled={!!successMessage}
          >
            <Upload size={14} className="mr-1" />
            Publish
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Entity info */}
        <div className="bg-surface-alt rounded-lg p-3">
          <div className="text-xs text-muted mb-1">{ENTITY_LABELS[entityType]}</div>
          <div className="font-medium text-heading text-sm">{entityName}</div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-heading mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this does and why others should use it..."
            rows={3}
            className="w-full px-3 py-2 bg-bg border rounded-lg text-body text-sm placeholder:text-faint focus:outline-hidden focus:ring-1 focus:ring-accent resize-none"
          />
        </div>

        {/* Category + Tags */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-heading mb-1">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Trading, Research"
              className="w-full px-3 py-2 bg-bg border rounded-lg text-body text-sm placeholder:text-faint focus:outline-hidden focus:ring-1 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-heading mb-1">Tags</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Comma-separated"
              className="w-full px-3 py-2 bg-bg border rounded-lg text-body text-sm placeholder:text-faint focus:outline-hidden focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>

        {/* Content access */}
        <div>
          <label className="block text-sm font-medium text-heading mb-1">Content access</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setContentAccess('private')}
              className={`text-left p-3 rounded-lg border text-xs transition-colors ${
                contentAccess === 'private' ? 'border-accent bg-accent-muted' : 'hover:bg-surface-alt'
              }`}
            >
              <p className="font-medium text-heading">Private</p>
              <p className="text-muted mt-0.5">Other orgs can run it. They never see or fork the content.</p>
            </button>
            <button
              type="button"
              onClick={() => setContentAccess('public')}
              className={`text-left p-3 rounded-lg border text-xs transition-colors ${
                contentAccess === 'public' ? 'border-accent bg-accent-muted' : 'hover:bg-surface-alt'
              }`}
            >
              <p className="font-medium text-heading">Public</p>
              <p className="text-muted mt-0.5">Other orgs can read the content and fork their own copy.</p>
            </button>
          </div>
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-heading mb-1">Pricing</label>
            <select
              value={pricingModel}
              onChange={(e) => setPricingModel(e.target.value)}
              className="w-full px-3 py-2 bg-bg border rounded-lg text-body text-sm focus:outline-hidden focus:ring-1 focus:ring-accent"
            >
              {PRICING_MODELS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {pricingModel === 'paid' && (
            <div>
              <label className="block text-sm font-medium text-heading mb-1">Price (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={priceUsd}
                onChange={(e) => setPriceUsd(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-bg border rounded-lg text-body text-sm placeholder:text-faint focus:outline-hidden focus:ring-1 focus:ring-accent"
              />
            </div>
          )}
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="bg-success-muted border rounded-lg p-3 text-success text-sm">{successMessage}</div>
        )}

        {/* Error message */}
        {publishMutation.isError && (
          <div className="bg-danger-muted border rounded-lg p-3 text-danger text-sm">
            Failed to publish. Please check your inputs and try again.
          </div>
        )}
      </div>
    </Modal>
  );
};
